import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { DataScroller } from 'primereact/datascroller';
import { Skeleton } from 'primereact/skeleton';
import { useHistory } from 'react-router-dom';
import { API_INSTANCE } from 'src/features/utils';
import { debounce } from 'lodash';
import styles from './GlobalAttendeeSearchModal.module.scss';
import { useAttendeeSidebarLogic } from 'src/redesign/features/AttendeesSidebar/views/AttendeeSidebarView/hooks/useAttendeeSidebarLogic';
import { getAttendeeDataById } from 'src/redesign/features/AttendeesSidebar/store/actions';
import { useAppDispatch } from 'src/hooks';

interface Attendee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  event: {
    id: string;
    title: string;
  };
  ticket?: {
    id: string;
    name: string;
  };
  checkout_cart?: {
    id: string;
    total: number;
  };
}

interface GlobalAttendeeSearchModalProps {
  visible: boolean;
  onHide: () => void;
}

export const GlobalAttendeeSearchModal: FC<GlobalAttendeeSearchModalProps> = ({
  visible,
  onHide,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<Attendee[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();

  const dispatch = useAppDispatch();

  // Focus search input when modal opens
  useEffect(() => {
    if (visible && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [visible]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setSearchQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [visible]);

  const searchAttendees = useCallback(async (query: string) => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await API_INSTANCE.get(`/api/v1/dashboard/attendees/search/all`, {
        params: { q: query.trim() },
      });
      setResults(response.data.results || []);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Failed to search attendees:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(debounce(searchAttendees, 300), [searchAttendees]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleAttendeeClick = async (attendee: Attendee) => {
    // Navigate to the attendee's event page with the attendee selected
    await dispatch(getAttendeeDataById(attendee?.id));
    history.push(`/events/manage/${attendee.event.id}/attendees/${attendee.id}`);
    onHide();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          handleAttendeeClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        onHide();
        break;
    }
  };

  const renderAttendeeItem = (attendee: Attendee, index: number) => {
    const isSelected = index === selectedIndex;

    return (
      <div
        key={attendee.id}
        className={`${styles.attendeeItem} ${isSelected ? styles.selected : ''}`}
        onClick={() => handleAttendeeClick(attendee)}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        <div className={styles.attendeeInfo}>
          <div className={styles.nameAndEmail}>
            <span className={styles.name}>
              {attendee.first_name} {attendee.last_name}
            </span>
            <span className={styles.email}>{attendee.email}</span>
          </div>
          <div className={styles.metadata}>
            <span className={styles.event}>
              <i className="pi pi-calendar" style={{ fontSize: '0.75rem', marginRight: '4px' }} />
              {attendee.event.title}
            </span>
            {attendee.ticket && (
              <span className={styles.ticket}>
                <i className="pi pi-ticket" style={{ fontSize: '0.75rem', marginRight: '4px' }} />
                {attendee.ticket.name}
              </span>
            )}
            <span className={`${styles.status} ${styles[attendee.status]}`}>{attendee.status}</span>
          </div>
        </div>
        <div className={styles.action}>
          <i className="pi pi-arrow-right" />
        </div>
      </div>
    );
  };

  const renderEmptyState = () => {
    if (loading) {
      return (
        <div className={styles.emptyState}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={styles.skeletonItem}>
              <Skeleton width="100%" height="60px" />
            </div>
          ))}
        </div>
      );
    }

    if (searchQuery.trim().length < 2) {
      return (
        <div className={styles.emptyState}>
          <i className="pi pi-search" style={{ fontSize: '2rem', color: '#6c757d' }} />
          <p>Type at least 2 characters to search attendees</p>
        </div>
      );
    }

    if (results.length === 0) {
      return (
        <div className={styles.emptyState}>
          <i className="pi pi-users" style={{ fontSize: '2rem', color: '#6c757d' }} />
          <p>No attendees found</p>
          <span className={styles.hint}>Try searching by name or email</span>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      className={styles.searchModal}
      dismissableMask
      showHeader={false}
      modal
      style={{ width: '600px', maxWidth: '90vw' }}
      contentClassName={styles.modalContent}
    >
      <div className={styles.searchContainer} onKeyDown={handleKeyDown}>
        <div className={styles.searchInputWrapper}>
          <i className="pi pi-search" />
          <InputText
            ref={searchInputRef}
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search attendees by name or email..."
            className={styles.searchInput}
          />
          <kbd className={styles.escapeHint}>ESC</kbd>
        </div>

        <div className={styles.resultsContainer}>
          {results.length > 0 ? (
            <div className={styles.resultsList}>
              {results.map((attendee, index) => renderAttendeeItem(attendee, index))}
            </div>
          ) : (
            renderEmptyState()
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.shortcuts}>
            <span>
              <kbd>↑</kbd>
              <kbd>↓</kbd> to navigate
            </span>
            <span>
              <kbd>↵</kbd> to select
            </span>
            <span>
              <kbd>ESC</kbd> to close
            </span>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
